import { Carousel, Typography } from 'antd';
import { useContext, useMemo, useEffect, useRef } from 'react';
import { AppContext, ScreenType } from '../../../App';
import { isNudgeValid } from '../../../Utils/nudgeUtils';
import styles from './index.module.css';
import { getAltJourney } from '../../../Utils/storageUtils';
import { altJourneyType } from '../../../Utils/types';
import { EVENTCODES, pushEventToPosthog } from '../../../Utils/eventUtils';
import { captureException } from '../../../Utils/sentryUtils';

const priorityMapping: Record<string, number> = {
  high: 0,
  medium: 1,
  low: 2,
};

const typeOrder = ['Cross Sell', 'User Engagement'];

const Nudge: React.FC<{
  title?: string;
  mock?: boolean;
  displayScreen?: ScreenType;
  allowedDisplayModes?: ('default' | 'banner')[];
}> = ({
  title = 'Insights for you',
  mock = false,
  displayScreen,
  allowedDisplayModes = ['default', 'banner'],
}) => {
  const { Text } = Typography;
  const { nudges }: any = useContext(AppContext);
  const viewedNudgesRef = useRef<Set<string>>(new Set());
  const currentNudgeIndexRef = useRef<number>(0);

  const trackNudgeViewed = (nudge: any) => {
    if (!nudge || viewedNudgesRef.current.has(nudge.nudgeId)) {
      return;
    }

    viewedNudgesRef.current.add(nudge.nudgeId);
    try {
      pushEventToPosthog({
        eventCode: EVENTCODES.NUDGE_VIEWED,
        meta: {
          category: nudge.category,
          screenType: displayScreen,
        },
      });
    } catch (error) {
      console.error('Error tracking nudge viewed:', error);
      captureException(error);
    }
  };

  const trackNudgeClicked = (nudge: any) => {
    try {
      pushEventToPosthog({
        eventCode: EVENTCODES.NUDGE_CLICKED,
        meta: {
          category: nudge.category,
          screenType: displayScreen,
        },
      });
    } catch (error) {
      console.error('Error tracking nudge clicked:', error);
      captureException(error);
    }
  };

  const isIOS = useMemo(() => {
    if (typeof window !== 'undefined' && window.navigator) {
      const userAgent = window.navigator.userAgent.toLowerCase();
      return /iphone|ipad|ipod|ios/.test(userAgent);
    }
    return false;
  }, []);

  const isYesOnline = useMemo(() => {
    return getAltJourney() === altJourneyType.YESONLINE;
  }, []);

  const getISTDate = () => {
    return new Date(
      new Date().toLocaleString('en-US', {
        timeZone: 'Asia/Kolkata',
      })
    );
  };

  const nudgesToDisplay = useMemo(() => {
    const filtered = nudges.filter((nudge: any) => {
      if (!isNudgeValid(nudge, getISTDate(), nudges)) {
        return false;
      }
      const screenToShow = nudge.meta?.screenToShow;
      const displayMode = nudge.meta?.displayMode;
      const campaign = nudge.campaignEnum;

      if (isIOS) {
        if (nudge.meta?.appCtaLink || nudge.meta?.webCtaLink) {
          return false;
        }
      }

      if (isYesOnline && displayMode === 'banner') {
        return false;
      }

      const matchesScreen =
        (Array.isArray(screenToShow) &&
          screenToShow.includes(displayScreen?.toLowerCase())) ||
        (typeof screenToShow === 'string' &&
          screenToShow.toLowerCase() === displayScreen?.toLowerCase());

      return (
        matchesScreen && allowedDisplayModes.includes(displayMode) && campaign
      );
    });

    const sorted = filtered.sort((a: any, b: any) => {
      const typeIndexA =
        typeOrder.indexOf(a.type) === -1
          ? typeOrder.length
          : typeOrder.indexOf(a.type);
      const typeIndexB =
        typeOrder.indexOf(b.type) === -1
          ? typeOrder.length
          : typeOrder.indexOf(b.type);

      if (typeIndexA !== typeIndexB) {
        return typeIndexA - typeIndexB;
      }

      const priorityA = priorityMapping[a.meta?.priority?.toLowerCase()] ?? 3;
      const priorityB = priorityMapping[b.meta?.priority?.toLowerCase()] ?? 3;

      return priorityA - priorityB;
    });

    const crossSellNudges = sorted.filter(
      (nudge: any) => nudge.type === 'Cross Sell'
    );
    const userEngagementNudges = sorted.filter(
      (nudge: any) => nudge.type === 'User Engagement'
    );

    const maxCrossSell = 4;
    const maxTotal = 5;

    const selectedCrossSell = crossSellNudges.slice(0, maxCrossSell);

    const remainingSlots = maxTotal - selectedCrossSell.length;
    const selectedUserEngagement = userEngagementNudges.slice(
      0,
      remainingSlots
    );
    return [...selectedCrossSell, ...selectedUserEngagement];
  }, [nudges, displayScreen, isIOS, isYesOnline, allowedDisplayModes]);

  useEffect(() => {
    if (nudgesToDisplay?.length > 0) {
      trackNudgeViewed(nudgesToDisplay[0]);
    }
  }, [nudgesToDisplay]);
  useEffect(() => {
    if (nudgesToDisplay?.length > 0) {
      nudgesToDisplay.forEach((nudge: any) => {
        try {
          pushEventToPosthog({
            eventCode: EVENTCODES.NUDGE_RENDER,
            meta: {
              category: nudge.category,
              screenType: displayScreen,
            },
          });
        } catch (error) {
          console.error('Error tracking nudge render:', error);
          captureException(error);
        }
      });
    }
  }, [nudgesToDisplay]);

  if (nudgesToDisplay.length === 0) return null;
  return (
    <div
      className={`${styles.nudgesRoot} ${
        displayScreen?.toLowerCase() === 'income' ||
        displayScreen?.toLowerCase() === 'investment' ||
        displayScreen?.toLowerCase() === 'expenditure'
          ? styles.noMargin
          : ''
      }`}
    >
      <Text className={`fontFamilyHeader ${styles.insightsTitle}`}>
        {title}
      </Text>
      <div className={styles.insightsRoot}>
        <Carousel
          id='yesBankNudge'
          className={mock ? 'mockNudges' : 'test'}
          infinite={false}
          draggable
          afterChange={(current) => {
            currentNudgeIndexRef.current = current;
            if (nudgesToDisplay[current]) {
              trackNudgeViewed(nudgesToDisplay[current]);
            }
          }}
        >
          {nudgesToDisplay.map((nudge: any, index: number) => {
            const isBanner = nudge.meta?.displayMode === 'banner';

            return (
              <div
                data-nudges-length={nudgesToDisplay.length}
                key={nudge.nudgeId}
                id={nudge.nudgeId}
                className={isBanner ? styles.bannerList : styles.nudgeList}
                onClick={() => {
                  trackNudgeClicked(nudge);
                  if (
                    nudge?.meta?.appCtaLink &&
                    (nudge?.meta?.appCtaLink.includes(
                      'https://irisbyyesbankuat.page.link'
                    ) ||
                      nudge?.meta?.appCtaLink.includes(
                        'https://irisbyyesbank.page.link'
                      ) ||
                      nudge?.meta?.appCtaLink.includes(
                        'https://irisbyyes-af.onelink.me'
                      )) &&
                    (() => {
                      try {
                        return Boolean(new URL(nudge.meta.appCtaLink));
                      } catch (error) {
                        captureException(error);
                        return false;
                      }
                    })()
                  ) {
                    window.open(nudge.meta.appCtaLink, '_self');
                  }
                }}
              >
                <div className={styles.insightContainer}>
                  {isBanner ? (
                    <div
                      id='nudge'
                      dangerouslySetInnerHTML={{
                        __html: nudge.body,
                      }}
                    />
                  ) : (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <Text
                            style={{
                              fontSize: 14,
                              fontWeight: 600,
                              flex: 1,
                              color: 'var(--text-color-white)',
                            }}
                          >
                            {nudge.title}
                          </Text>
                        </div>
                        <Text
                          style={{
                            color: 'var(--text-color-white)',
                            fontSize: 10,
                            fontWeight: 600,
                            opacity: 0.7,
                          }}
                        >
                          {nudge.body}
                        </Text>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </Carousel>
      </div>
    </div>
  );
};

export default Nudge;
